import React from "react";
import { View, Text, ScrollView } from "react-native";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";

import Colors from "../../../Resources/Color";
import Fonts from "../../../Resources/Fonts";

const HeaderCell = ({ width, flex, minWidth, title }) => {
  const cellStyle =
    typeof width === "number"
      ? { width, minWidth: width }
      : {
          flex: typeof flex === "number" ? flex : 1,
          minWidth: typeof minWidth === "number" ? minWidth : 100,
        };

  return (
    <View
      style={{
        padding: 10,
        justifyContent: "center",
        ...cellStyle,
      }}
    >
      <Text
        style={{
          textAlign: "center",
          textTransform: "uppercase",
          fontSize: 14,
          fontWeight: "600",
          color: Colors.darkgrayColor,
        }}
        numberOfLines={1}
      >
        {title}
      </Text>
    </View>
  );
};

const BodyCell = ({
  width,
  flex,
  minWidth,
  value,
  center = true,
  pre = false,
  textStyle,
  children,
}) => {
  const cellStyle =
    typeof width === "number"
      ? { width, minWidth: width }
      : {
          flex: typeof flex === "number" ? flex : 1,
          minWidth: typeof minWidth === "number" ? minWidth : 100,
        };

  return (
    <View
      style={{
        padding: 10,
        justifyContent: "center",
        alignItems: center ? "center" : "flex-start",
        ...cellStyle,
      }}
    >
      {children ? (
        children
      ) : (
        <Text
          style={[
            Fonts.regular12,
            {
              fontSize: 12,
              color: Colors.blackColor,
              textAlign: center ? "center" : "left",
              whiteSpace: pre ? "pre" : "normal",
            },
            textStyle,
          ]}
        >
          {value}
        </Text>
      )}
    </View>
  );
};

const ExerciseLabel = ({
  store,
  t,
  exerciseDisplayName,
  protocolSlot,
  occurrenceLabel,
  fallbackLabel,
  center = true,
  size = "row", // "row" | "group"
  color,
  dotColor,
  showDot = true,
}) => {
  const baseName = String(exerciseDisplayName ?? "").trim();
  const slotText = protocolSlot ? String(t(protocolSlot) ?? "").trim() : "";
  const labelFallback = String(fallbackLabel ?? "").trim();

  const resolvedColor =
    color ??
    (baseName
      ? store?.getColorForExerciseAndSlot?.(baseName, protocolSlot ?? null)
      : null) ??
    Colors.bubbleColor;

  const resolvedDotColor = dotColor ?? resolvedColor;

  const titleFontSize = size === "group" ? 13 : 12;
  const slotFontSize = size === "group" ? 12 : 11;
  const dotFontSize = size === "group" ? 10 : 9;
  const dotVerticalOffset = 1;

  // If we don't have structured fields, fall back to the original label string.
  const showFallbackOnly = !baseName && labelFallback;

  // Single wrapping text run: dot + name + slot all wrap together naturally.
  return (
    <Text
      style={[
        Fonts.regular12,
        {
          textAlign: center ? "center" : "left",
        },
      ]}
    >
      {showDot ? (
        <Text
          style={{
            color: resolvedDotColor,
            fontSize: dotFontSize,
            marginRight: 4,
            transform: [{ translateY: dotVerticalOffset }],
          }}
        >
          {"\u25CF"}
        </Text>
      ) : null}

      {showFallbackOnly ? (
        <Text
          style={{
            fontSize: titleFontSize,
            fontWeight: "600",
            color: Colors.blackColor,
          }}
        >
          {labelFallback}
        </Text>
      ) : (
        <>
          <Text
            style={{
              fontSize: titleFontSize,
              fontWeight: "600",
              color: Colors.blackColor,
            }}
          >
            {baseName}
          </Text>
          {slotText ? (
            <Text
              style={{
                fontSize: slotFontSize,
                color: Colors.grayColor,
                fontWeight: "400",
              }}
            >
              {`\u00A0·\u00A0${slotText}`}
            </Text>
          ) : null}
          {occurrenceLabel ? (
            <Text
              style={{
                fontSize: slotFontSize,
                color: Colors.grayColor,
                fontWeight: "400",
              }}
            >
              {`\u00A0${String(occurrenceLabel).trim()}`}
            </Text>
          ) : null}
        </>
      )}
    </Text>
  );
};

const BlockLabel = ({ title, color, center = true, size = "group" }) => {
  const titleFontSize = size === "group" ? 13 : 12;
  const dotFontSize = size === "group" ? 10 : 9;
  const dotVerticalOffset = 1;

  return (
    <Text
      style={[
        Fonts.regular12,
        {
          textAlign: center ? "center" : "left",
        },
      ]}
    >
      <Text
        style={{
          color: color ?? Colors.bubbleColor,
          fontSize: dotFontSize,
          marginRight: 4,
          transform: [{ translateY: dotVerticalOffset }],
        }}
      >
        {"\u25CF"}
      </Text>
      <Text>{"\u00A0"}</Text>
      <Text
        style={{
          fontSize: titleFontSize,
          fontWeight: "600",
          color: Colors.blackColor,
        }}
      >
        {String(title ?? "")}
      </Text>
    </Text>
  );
};

const SkeletonRow = ({ columns }) => {
  return (
    <View
      style={{
        flexDirection: "row",
        borderBottomWidth: 0.1,
        borderColor: Colors.borderColor,
        height: 50,
        alignItems: "center",
      }}
    >
      {columns.map((c, idx) => {
        const cellStyle =
          typeof c.width === "number"
            ? { width: c.width, minWidth: c.width }
            : {
                flex: typeof c.flex === "number" ? c.flex : 1,
                minWidth: typeof c.minWidth === "number" ? c.minWidth : 100,
              };

        return (
          <View
            key={idx}
            style={{
              padding: 10,
              justifyContent: "center",
              alignItems: "center",
              ...cellStyle,
            }}
          >
            <View
              style={{
                width: "60%",
                height: 12,
                backgroundColor: Colors.lightGrayColor,
                borderRadius: 4,
              }}
            />
          </View>
        );
      })}
    </View>
  );
};

const ClientStatisticsTable = observer(({ store }) => {
  const { t, i18n } = useTranslation();

  const model = store?.getDebugTableModel
    ? store.getDebugTableModel({ t, language: i18n.language })
    : { columns: [], groups: [], mode: store?.mode };

  let columns = model?.columns ?? [];
  const groups = model?.groups ?? [];

  const mode =
    model?.mode ?? (store?.selectedBlockIds?.length > 0 ? "weeks" : "time");

  if (columns.length === 0) {
    if (mode === "weeks") {
      const isMultiBlock = (store?.selectedBlockIds?.length ?? 0) > 1;
      const start = store?.startWeek ?? 1;
      const end = store?.endWeek ?? start;

      columns = [
        {
          key: isMultiBlock ? "blockHeader-string" : "exerciseHeader-string",
          title: isMultiBlock
            ? t("blockHeader-string")
            : t("exerciseHeader-string"),
          minWidth: 220,
          flex: 1,
        },
        ...(isMultiBlock
          ? [
              {
                key: "exerciseHeader-string",
                title: t("exerciseHeader-string"),
                minWidth: 220,
                flex: 1,
              },
            ]
          : []),
        {
          key: "metricsHeader-string",
          title: t("metricsHeader-string"),
          minWidth: 140,
          flex: 1,
        },
      ];
      for (let w = start; w <= end; w++) {
        const wk = t("wOfExercise-string", { week: w });
        columns.push({ key: wk, title: wk, flex: 1, minWidth: 140 });
      }
    } else {
      columns = [
        {
          key: "dateHeader-string",
          title: t("dateHeader-string"),
          width: 120,
        },
        {
          key: "exerciseHeader-string",
          title: t("exerciseHeader-string"),
          flex: 1,
          minWidth: 220,
        },
        {
          key: "setsHeader-string",
          title: t("setsHeader-string"),
          flex: 1,
          minWidth: 160,
        },
        {
          key: "e1rm-string",
          title: t("e1rm-string"),
          flex: 1,
          minWidth: 130,
          isMetric: true,
          metricKey: "e1rm",
        },
        {
          key: "stressIndex-string",
          title: t("stressIndex-string"),
          flex: 1,
          minWidth: 130,
          isMetric: true,
          metricKey: "stressIndex",
        },
        {
          key: "tonnage-string",
          title: t("tonnage-string"),
          flex: 1,
          minWidth: 130,
          isMetric: true,
          metricKey: "tonnage",
        },
      ];
    }
  }

  // TIME MODE: grouped by date (legacy DatesTable pattern)
  const isTimeHeader = columns?.[0]?.key === "dateHeader-string";
  if (!model?.mode || model?.mode === "time" || isTimeHeader) {
    const displayColumns = (columns ?? []).filter(
      (c) => !(c?.isMetric && c?.metricKey === "none")
    );

    const dateCol = displayColumns[0];
    const restCols = displayColumns.slice(1);

    return (
      <ScrollView
        horizontal={true}
        showsHorizontalScrollIndicator={false}
        style={{ marginTop: 12, flex: 1, flexDirection: "row" }}
        contentContainerStyle={{ flex: 1, flexDirection: "row" }}
      >
        <View
          style={{
            backgroundColor: Colors.whiteColor,
            borderTopLeftRadius: 14,
            borderTopRightRadius: 14,
            borderBottomLeftRadius: 14,
            borderBottomRightRadius: 14,
            flexGrow: 1,
            borderWidth: 0.1,
            borderColor: Colors.borderColor,
            overflow: "hidden",
          }}
        >
          <View
            style={{
              flexDirection: "row",
              backgroundColor: Colors.lightGrayColor,
              height: 54,
              borderTopLeftRadius: 14,
              borderTopRightRadius: 14,
              borderBottomWidth: 0.1,
              borderBottomColor: Colors.borderColor,
              alignItems: "center",
            }}
          >
            {displayColumns.map((c) => (
              <HeaderCell
                key={c.key}
                width={c.width}
                flex={c.flex}
                minWidth={c.minWidth}
                title={c.title}
              />
            ))}
          </View>

          {groups.length > 0
            ? groups.map((g, groupIdx) => (
                <View
                  key={g.key ?? groupIdx}
                  style={{
                    flexDirection: "row",
                    borderBottomWidth: 0.1,
                    borderBottomColor: Colors.borderColor,
                  }}
                >
                  <View
                    style={{
                      width: dateCol?.width ?? 120,
                      minWidth: dateCol?.width ?? 120,
                      justifyContent: "center",
                    }}
                  >
                    <View
                      style={{ justifyContent: "center", alignItems: "center" }}
                    >
                      <Text style={{ textAlign: "center", fontSize: 12 }}>
                        {g.dateLabel ?? ""}
                      </Text>
                    </View>
                  </View>

                  <View style={{ flex: 9 }}>
                    {(g.rows ?? []).map((r, idx) => (
                      <View key={idx} style={{ flexDirection: "row" }}>
                        {restCols.map((c) => {
                          const raw = r?.[c.key];
                          const isSets = c.key === "setsHeader-string";
                          const isExercise = c.key === "exerciseHeader-string";
                          const isMetric = Boolean(c.isMetric);
                          const valueNumber =
                            typeof raw === "number" ? raw : Number(raw);
                          const value = isMetric
                            ? isFinite(valueNumber) && valueNumber > 0
                              ? store?._formatLabel?.(valueNumber) ??
                                String(valueNumber)
                              : "-"
                            : String(raw ?? "-");

                          const metricValueStyle =
                            isMetric && value !== "-"
                              ? {
                                  color: Colors.blackColor,
                                  fontWeight: "400",
                                }
                              : undefined;

                          return (
                            <BodyCell
                              key={c.key}
                              width={c.width}
                              flex={c.flex}
                              minWidth={c.minWidth}
                              value={value}
                              center={true}
                              pre={isSets}
                              textStyle={
                                isSets
                                  ? { color: Colors.grayColor }
                                  : metricValueStyle
                              }
                            >
                              {isExercise ? (
                                <ExerciseLabel
                                  store={store}
                                  t={t}
                                  exerciseDisplayName={r?.__exerciseDisplayName}
                                  protocolSlot={r?.__protocolSlot}
                                  occurrenceLabel={r?.__occurrenceLabel}
                                  fallbackLabel={value}
                                  center={true}
                                  size={"row"}
                                  color={r?.__seriesColor}
                                />
                              ) : null}
                            </BodyCell>
                          );
                        })}
                      </View>
                    ))}
                  </View>
                </View>
              ))
            : Array.from({ length: 2 }).map((_, i) => (
                <SkeletonRow key={i} columns={displayColumns} />
              ))}
        </View>
      </ScrollView>
    );
  }

  // WEEKS MODE: grouped by exercise with METRICS + week columns (legacy BlocksTable pattern)
  const isMultiBlockWeeks = columns?.[0]?.key === "blockHeader-string";

  const leftCol = columns[0];
  const exerciseCol = isMultiBlockWeeks ? columns[1] : columns[0];
  const metricsCol = isMultiBlockWeeks ? columns[2] : columns[1];
  const weekCols = isMultiBlockWeeks ? columns.slice(3) : columns.slice(2);

  const rightSideFlex = 1 + (weekCols?.length ?? 0);

  return (
    <ScrollView
      horizontal={true}
      showsHorizontalScrollIndicator={false}
      style={{ marginTop: 12, flex: 1, flexDirection: "row" }}
      contentContainerStyle={{ flex: 1, flexDirection: "row" }}
    >
      <View
        style={{
          backgroundColor: Colors.whiteColor,
          borderTopLeftRadius: 14,
          borderTopRightRadius: 14,
          borderBottomLeftRadius: 14,
          borderBottomRightRadius: 14,
          flexGrow: 1,
          borderWidth: 0.1,
          borderColor: Colors.borderColor,
          overflow: "hidden",
        }}
      >
        <View
          style={{
            flexDirection: "row",
            backgroundColor: Colors.lightGrayColor,
            height: 54,
            // borderTopLeftRadius: 14,
            // borderTopRightRadius: 14,
            borderBottomWidth: 0.1,
            borderBottomColor: Colors.borderColor,
            alignItems: "center",
          }}
        >
          {isMultiBlockWeeks ? (
            <>
              <HeaderCell
                key={leftCol?.key}
                width={leftCol?.width}
                flex={leftCol?.flex}
                minWidth={leftCol?.minWidth}
                title={leftCol?.title}
              />
              <HeaderCell
                key={exerciseCol?.key}
                width={exerciseCol?.width}
                flex={exerciseCol?.flex}
                minWidth={exerciseCol?.minWidth}
                title={exerciseCol?.title}
              />
              <View style={{ flex: rightSideFlex, flexDirection: "row" }}>
                <HeaderCell
                  key={metricsCol?.key}
                  width={metricsCol?.width}
                  flex={metricsCol?.flex}
                  minWidth={metricsCol?.minWidth}
                  title={metricsCol?.title}
                />
                {weekCols.map((c) => (
                  <HeaderCell
                    key={c.key}
                    width={c.width}
                    flex={c.flex}
                    minWidth={c.minWidth}
                    title={c.title}
                  />
                ))}
              </View>
            </>
          ) : (
            <>
              <HeaderCell
                key={exerciseCol?.key}
                width={exerciseCol?.width}
                flex={exerciseCol?.flex}
                minWidth={exerciseCol?.minWidth}
                title={exerciseCol?.title}
              />
              <View style={{ flex: rightSideFlex, flexDirection: "row" }}>
                <HeaderCell
                  key={metricsCol?.key}
                  width={metricsCol?.width}
                  flex={metricsCol?.flex}
                  minWidth={metricsCol?.minWidth}
                  title={metricsCol?.title}
                />
                {weekCols.map((c) => (
                  <HeaderCell
                    key={c.key}
                    width={c.width}
                    flex={c.flex}
                    minWidth={c.minWidth}
                    title={c.title}
                  />
                ))}
              </View>
            </>
          )}
        </View>

        {groups.length > 0
          ? groups.map((g, groupIdx) => (
              <View
                key={g.key ?? groupIdx}
                style={{
                  flexDirection: "row",
                  borderBottomWidth: 0.1,
                  borderBottomColor: Colors.borderColor,
                }}
              >
                {isMultiBlockWeeks ? (
                  <View
                    style={{
                      flex:
                        typeof leftCol?.flex === "number" ? leftCol.flex : 1,
                      minWidth:
                        typeof leftCol?.minWidth === "number"
                          ? leftCol.minWidth
                          : 220,
                      justifyContent: "center",
                      alignItems: "center",
                      padding: 10,
                    }}
                  >
                    {(() => {
                      const isSlotSplit = Boolean(g?.__isSlotSplitWithinBlock);
                      // In multi-block + slot-split rows, the chart legend uses the
                      // series (exercise+slot) color. Mirror that by showing the
                      // series dot next to the BLOCK label and hiding the dot in
                      // the EXERCISE column.
                      const blockDotColor =
                        (isSlotSplit
                          ? g?.__seriesColor
                          : g?.__blockSeriesColor) ?? g?.__blockSeriesColor;

                      return (
                        <BlockLabel
                          title={g.title ?? ""}
                          color={blockDotColor}
                          center={true}
                          size={"group"}
                        />
                      );
                    })()}
                  </View>
                ) : (
                  <View
                    style={{
                      flex:
                        typeof exerciseCol?.flex === "number"
                          ? exerciseCol.flex
                          : 1,
                      minWidth:
                        typeof exerciseCol?.minWidth === "number"
                          ? exerciseCol.minWidth
                          : 220,
                      justifyContent: "center",
                      alignItems: "center",
                      padding: 10,
                    }}
                  >
                    <ExerciseLabel
                      store={store}
                      t={t}
                      exerciseDisplayName={g?.__exerciseDisplayName}
                      protocolSlot={g?.__protocolSlot}
                      fallbackLabel={g.title ?? ""}
                      center={true}
                      size={"group"}
                      color={g?.__seriesColor}
                      showDot={true}
                    />
                  </View>
                )}

                {isMultiBlockWeeks ? (
                  <View
                    style={{
                      flex:
                        typeof exerciseCol?.flex === "number"
                          ? exerciseCol.flex
                          : 1,
                      minWidth:
                        typeof exerciseCol?.minWidth === "number"
                          ? exerciseCol.minWidth
                          : 220,
                      justifyContent: "center",
                      alignItems: "center",
                      padding: 10,
                    }}
                  >
                    {(() => {
                      const isSlotSplit = Boolean(g?.__isSlotSplitWithinBlock);
                      return (
                        <ExerciseLabel
                          store={store}
                          t={t}
                          exerciseDisplayName={g?.__exerciseDisplayName}
                          protocolSlot={g?.__protocolSlot}
                          fallbackLabel={g.exerciseTitle ?? g.title ?? ""}
                          center={true}
                          size={"group"}
                          color={g?.__seriesColor}
                          // In multi-block weeks layout, the dot belongs to the
                          // BLOCK column (matching the legend intent). Never
                          // show a dot in the EXERCISE column.
                          showDot={false}
                        />
                      );
                    })()}
                  </View>
                ) : null}

                <View style={{ flex: rightSideFlex }}>
                  {(g.rows ?? [])
                    .filter((r) => r?.__metricKey !== "none")
                    .map((r, rowIdx) => (
                      <View key={rowIdx} style={{ flexDirection: "row" }}>
                        {(() => {
                          const label = String(r.__label ?? "");
                          const isSetsRow = label === String(t("sets-string"));
                          const isE1rmRow = label === String(t("e1rm-string"));
                          const isStressRow =
                            label === String(t("stressIndex-string"));
                          const isTonnageRow =
                            label === String(t("tonnage-string"));

                          const metricsLabelStyle = isSetsRow
                            ? { color: Colors.grayColor, fontWeight: "400" }
                            : isE1rmRow || isStressRow || isTonnageRow
                            ? { color: Colors.blackColor, fontWeight: "400" }
                            : { color: Colors.blackColor, fontWeight: "400" };

                          const metricValueStyleForRow = (value) => {
                            if (isSetsRow) return { color: Colors.grayColor };
                            if (!value || value === "-" || value === "0")
                              return undefined;
                            if (isE1rmRow || isStressRow || isTonnageRow)
                              return {
                                color: Colors.blackColor,
                                fontWeight: "400",
                              };
                            return {
                              color: Colors.blackColor,
                              fontWeight: "400",
                            };
                          };

                          return (
                            <>
                              <BodyCell
                                width={metricsCol?.width}
                                flex={metricsCol?.flex}
                                minWidth={metricsCol?.minWidth}
                                value={label}
                                center={true}
                                textStyle={metricsLabelStyle}
                              />
                              {weekCols.map((c) => {
                                const rawValue = isSetsRow
                                  ? String(r[c.key] ?? "")
                                  : r[c.key] && r[c.key] !== "0"
                                  ? String(r[c.key])
                                  : "-";

                                return (
                                  <BodyCell
                                    key={c.key}
                                    width={c.width}
                                    flex={c.flex}
                                    minWidth={c.minWidth}
                                    value={rawValue}
                                    center={true}
                                    pre={true}
                                    textStyle={metricValueStyleForRow(rawValue)}
                                  />
                                );
                              })}
                            </>
                          );
                        })()}
                      </View>
                    ))}
                </View>
              </View>
            ))
          : Array.from({ length: 2 }).map((_, i) => (
              <SkeletonRow key={i} columns={columns} />
            ))}
      </View>
    </ScrollView>
  );
});

export default ClientStatisticsTable;
